import { DateTime } from 'luxon';

import { convertTimestampToArray } from '..';
import {
  foldLine,
  formatDate,
  formatDuration,
  setAlarm,
  setContact,
  setDescription,
  setGeolocation,
  setLocation,
  setOrganizer,
  setSummary,
} from '../utils';
import formatText from '../utils/format-text';

const formatICSDate2 = (dateParts: number[], dateZone: string) => {
  // console.log('formatICSDate', { dateParts, dateZone, ouTimezone });
  const date = DateTime.local(dateParts[0], dateParts[1], dateParts[2], dateParts[3], dateParts[4], dateParts[5], {
    zone: dateZone,
  });
  const formatted = date.toFormat("yyyyMMdd'T'HHmmss");
  return formatted;
};

const formatICSDate = (dateParts: number[], dateZone: string, ouTimezone: string) => {
  // console.log('formatICSDate', { dateParts, dateZone, ouTimezone });
  const date = DateTime.local(dateParts[0], dateParts[1], dateParts[2], dateParts[3], dateParts[4], dateParts[5], {
    zone: ouTimezone,
  }).setZone(dateZone);
  const formatted = date.toFormat("yyyyMMdd'T'HHmmss");
  return formatted;
};

export default function formatEvent(attributes: any = {}) {
  // console.log('formatEvent', { attributes });
  const {
    title,
    productId,
    method,
    uid,
    sequence,
    timestamp,
    startTimezone,
    startDate,
    start,
    startType,
    startInputType,
    startOutputType,
    duration,
    endTimezone,
    endDate,
    end,
    endInputType,
    endOutputType,
    description,
    url,
    geo,
    location,
    status,
    categories,
    organizer,
    attendees,
    alarms,
    recurrenceRule,
    busyStatus,
    classification,
    created,
    lastModified,
    calName,
    htmlContent,
    timezone,
    exdate,
    attachedFuelEntityType,
    attachedFuelEntityId,
    attachedFuelDisplayClassName,
    attachedFuelEventFlags,
    attachedFuelGraphicsConfig,
    attachedFuelCustomAttributes,
    fieldDictionary,
    subTitle,
  } = attributes;

  let icsFormat = '';
  icsFormat += 'BEGIN:VCALENDAR\r\n';
  icsFormat += 'CALSCALE:GREGORIAN\r\n';
  icsFormat += `${foldLine(`METHOD:${method}`)}\r\n`;
  icsFormat += `${foldLine(`PRODID:${productId}`)}\r\n`;
  icsFormat += 'VERSION:2.0\r\n';
  icsFormat += calName ? `${foldLine(`X-WR-CALNAME:${calName}`)}\r\n` : '';
  icsFormat += `X-PUBLISHED-TTL:PT1H\r\n`;
  icsFormat += `X-WR-TIMEZONE:${timezone}\r\n`;
  icsFormat += 'BEGIN:VEVENT\r\n';
  icsFormat += `UID:${uid}\r\n`;
  icsFormat += `${foldLine(`SUMMARY:${title ? setSummary(title) : title}`)}\r\n`;
  icsFormat += `DTSTAMP:${timestamp}\r\n`;

  const toStartTimezone = startTimezone || timezone;
  icsFormat += `${foldLine(
    `DTSTART;TZID=${toStartTimezone}${start && start.length == 3 ? ';VALUE=DATE' : ''}:${formatICSDate(
      start,
      toStartTimezone,
      timezone
    )}`
  )}\r\n`;

  // End is not required for all day events on single days (like anniversaries)
  if (!end || end.length !== 3 || start.length !== end.length || start.some((val, i) => val !== end[i])) {
    if (end) {
      const toEndTimezone = endTimezone || timezone;
      icsFormat += `${foldLine(
        `DTEND;TZID=${toEndTimezone}${end.length === 3 ? ';VALUE=DATE' : ''}:${formatICSDate(end, toEndTimezone, timezone)}`
      )}\r\n`;
      // icsFormat += `DTEND;TZID="${timezone}"${end.length === 3 ? ';VALUE=DATE' : ''}:${formatICSDate(end, null, timezone)}\r\n`;
    }
  }

  icsFormat += fieldDictionary.categories === true ? `${foldLine(`CATEGORIES:${categories}`)}\r\n` : '';
  icsFormat += fieldDictionary.description === true ? `${foldLine(`DESCRIPTION:${setDescription(description)}`)}\r\n` : '';

  // // All day events like anniversaries must be specified as VALUE type DATE
  // icsFormat += foldLine(`DTSTART;TZID="${timezone}"${start && start.length == 3 ? ';VALUE=DATE' : ''}:${formatICSDate(
  //   start,
  //   null,
  //   timezone
  // )}`) + '\r\n';
  icsFormat += recurrenceRule ? `${foldLine(`RRULE:${recurrenceRule}`)}\r\n` : '';

  if (attachedFuelEntityType) {
    icsFormat += `${foldLine(`X-ATTACHED-FUEL-ENTITY:${formatText(attachedFuelEntityId)}`)}\r\n`;
  }
  if (attachedFuelDisplayClassName) {
    icsFormat += `${foldLine(`X-ATTACHED-FUEL-DISPLAY-CLASS:${formatText(attachedFuelDisplayClassName)}`)}\r\n`;
  }
  if (attachedFuelEventFlags) {
    icsFormat += `${foldLine(`X-ATTACHED-FUEL-EVENT-FLAGS:${formatText(attachedFuelEventFlags)}`)}\r\n`;
  }
  if (attachedFuelGraphicsConfig) {
    icsFormat += `${foldLine(`X-ATTACHED-FUEL-GRAPHICS-CONFIG:${formatText(attachedFuelGraphicsConfig)}`)}\r\n`;
  }
  if (attachedFuelCustomAttributes) {
    icsFormat += `${foldLine(`X-ATTACHED-FUEL-CUSTOM-ATTRIBUTES:${formatText(attachedFuelCustomAttributes)}`)}\r\n`;
  }
  if (subTitle) {
    icsFormat += `${foldLine(`X-SUBTITLE:${formatText(subTitle)}`)}\r\n`;
  }

  icsFormat += typeof sequence !== 'undefined' ? `SEQUENCE:${sequence}\r\n` : '';
  icsFormat += url ? `${foldLine(`URL:${url}`)}\r\n` : '';
  icsFormat += geo ? `${foldLine(`GEO:${setGeolocation(geo)}`)}\r\n` : '';
  icsFormat += location ? `${foldLine(`LOCATION:${setLocation(location)}`)}\r\n` : '';
  icsFormat += status ? `${foldLine(`STATUS:${status}`)}\r\n` : '';
  icsFormat += organizer ? `${foldLine(`ORGANIZER;${setOrganizer(organizer)}`)}\r\n` : '';
  icsFormat += busyStatus ? `${foldLine(`X-MICROSOFT-CDO-BUSYSTATUS:${busyStatus}`)}\r\n` : '';
  icsFormat += classification ? `${foldLine(`CLASS:${classification}`)}\r\n` : '';
  icsFormat += created ? `CREATED:${formatDate(created)}\r\n` : '';
  icsFormat += lastModified ? `LAST-MODIFIED:${formatDate(lastModified)}\r\n` : '';
  icsFormat += htmlContent ? `${foldLine(`X-ALT-DESC;FMTTYPE=text/html:${htmlContent}`)}\r\n` : '';
  if (attendees) {
    attendees.map(function (attendee) {
      icsFormat += `${foldLine(`ATTENDEE;${setContact(attendee)}`)}\r\n`;
    });
  }
  icsFormat += duration ? `${foldLine(`DURATION:${formatDuration(duration)}`)}\r\n` : '';
  if (alarms) {
    alarms.map(function (alarm) {
      icsFormat += setAlarm(alarm);
    });
  }
  if (exdate && exdate.length > 0) {
    exdate.map(function (date) {
      let newexdate = convertTimestampToArray(date).includes(NaN) ? date : convertTimestampToArray(date);
      if (!convertTimestampToArray(date).includes(NaN)) {
        // #2546 aalt 2025-01-17 keep the exdates in their original timezone
        newexdate = formatICSDate2(newexdate, toStartTimezone); // formatDate(newexdate, startOutputType || startType, startInputType);
        // newexdate = formatICSDate(newexdate, toStartTimezone, timezone); //formatDate(newexdate, startOutputType || startType, startInputType);
      }
      icsFormat += `${foldLine(
        `EXDATE;TZID=${toStartTimezone}${newexdate && newexdate.length == 3 ? ';VALUE=DATE' : ''}:${newexdate}`
      )}\r\n`;
    });
  }

  icsFormat += `END:VEVENT\r\n`;
  icsFormat += `END:VCALENDAR\r\n`;

  // console.log('formatEvent', { icsFormat });

  return icsFormat;
}
